import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { detectToolsOrigin } from "../../../utils/helper.js";

import LeadGateModal from "../Modals/LeadGateModal.jsx";
import SuccessModal from "../Modals/SuccessModal.jsx";
import ConfirmSendResultsModal from "../Modals/ConfirmSendResultsModal.jsx";
import UpdateEmailModal from "../Modals/UpdateEmailModal.jsx";

import "../calculator.scss";

const mortgageDisclaimerText = (year) =>
  "Key Assumptions & Important Notes\n\n" +
  "Key Assumptions:\n" +
  `• Based on ${year} IRS mortgage interest deduction rules\n` +
  "• HELOC interest only deductible if used to buy, build, or substantially improve your home\n" +
  "• Tax savings estimate uses a flat 24% marginal rate (for illustration)\n" +
  "• Does not consider state taxes, AMT limitations, or other phaseouts\n" +
  "• Consult a tax professional for personalized guidance\n\n" +
  "This calculator provides estimates for planning purposes only. Actual outcomes may vary based on market conditions, taxes, fees, and individual circumstances.";

// ✅ SAME AS OTHERS: proxy url
const PROXY_URL =
  window.__UK_PROXY_URL__ || `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

function MortgageInterestDeductionCalculator({
  year = 2025,
  redirectUrl = "https://unclekam.com/contact-us/",
  calculatorName = "Mortgage Interest Deduction Calculator",
  endpoint = PROXY_URL, // kept for compatibility
}) {
  // ✅ UPDATED: spaced/capitalized values everywhere
  const [filingStatus, setFilingStatus] = useState("Single");

  const [acquisitionDate, setAcquisitionDate] = useState("after");
  const [originalMortgageAmount, setOriginalMortgageAmount] = useState("");
  const [currentMortgageBalance, setCurrentMortgageBalance] = useState("");
  const [annualMortgageInterest, setAnnualMortgageInterest] = useState("");
  const [helocBalance, setHelocBalance] = useState("");

  // ✅ FIX: value must match comparison later (was "Home improvement" vs "home_improvement")
  const [helocPurpose, setHelocPurpose] = useState("Home Improvement");

  const [annualHelocInterest, setAnnualHelocInterest] = useState("");

  const [results, setResults] = useState(null);
  const [hasCalculated, setHasCalculated] = useState(false);

  const latestPayloadRef = useRef({ inputs_json: null, results_json: null });
  const resultsRef = useRef(null);

  // ✅ UPDATED KEYS to spaced values
  const STANDARD_DEDUCTIONS_BY_YEAR = {
    2025: {
      Single: 15000,
      "Married Filing Jointly": 30000,
      "Married Filing Separately": 15000,
      "Head of Household": 22500,
      "Qualifying Widow(er)": 30000,
    },
    2026: {
      Single: 16100,
      "Married Filing Jointly": 32200,
      "Married Filing Separately": 16100,
      "Head of Household": 24150,
      "Qualifying Widow(er)": 32200,
    },
  };

  const standardDeductions =
    STANDARD_DEDUCTIONS_BY_YEAR[year] || STANDARD_DEDUCTIONS_BY_YEAR[2025];

  const getDebtLimit = (status, acqDate) => {
    const isMFS = status === "Married Filing Separately";
    if (acqDate === "before") return isMFS ? 500000 : 1000000;
    return isMFS ? 375000 : 750000;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // ----------- GATING (shared key + 24h TTL) -----------
  const UNK_GATE_KEY = "unk_calc_unlock_v1";
  const UNK_GATE_TTL_MS = 24 * 60 * 60 * 1000;

  const readUnlock = () => {
    try {
      const raw = localStorage.getItem(UNK_GATE_KEY);
      if (!raw) return { unlocked: false, email: "" };
      const parsed = JSON.parse(raw);
      const expiresAt = Number(parsed?.expiresAt || 0);
      const email = String(parsed?.email || "");
      if (!expiresAt || Date.now() > expiresAt) {
        localStorage.removeItem(UNK_GATE_KEY);
        return { unlocked: false, email: "" };
      }
      return { unlocked: true, email };
    } catch {
      return { unlocked: false, email: "" };
    }
  };

  const writeUnlock = ({ email }) => {
    const payload = {
      expiresAt: Date.now() + UNK_GATE_TTL_MS,
      email: String(email || ""),
    };
    localStorage.setItem(UNK_GATE_KEY, JSON.stringify(payload));
  };

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";

  const [unlocked, setUnlocked] = useState(false);
  const [storedEmail, setStoredEmail] = useState("");

  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false);

  const [sendingResults, setSendingResults] = useState(false);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const qa = useMemo(() => params.has("qa_mode"), [params]);

  useEffect(() => {
    const gate = readUnlock();
    setUnlocked(gate.unlocked);
    setStoredEmail(gate.email || "");
  }, []);

  // ✅ if user changes any input after calculating, invalidate results
  useEffect(() => {
    setHasCalculated(false);
    setResults(null);
    latestPayloadRef.current = { inputs_json: null, results_json: null };
  }, [
    filingStatus,
    acquisitionDate,
    originalMortgageAmount,
    currentMortgageBalance,
    annualMortgageInterest,
    helocBalance,
    helocPurpose,
    annualHelocInterest,
    year,
  ]);

  const scrollToResults = () => {
    const el = resultsRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // ✅ unchanged calculation logic, returns payload
  const computePayload = () => {
    const mortgageBalance = parseFloat(currentMortgageBalance) || 0;
    const mortgageInterest = parseFloat(annualMortgageInterest) || 0;
    const helocBal = parseFloat(helocBalance) || 0;
    const helocInterest = parseFloat(annualHelocInterest) || 0;

    const maxDebt = getDebtLimit(filingStatus, acquisitionDate);

    let deductibleMortgageInt = 0;
    if (mortgageBalance > 0 && mortgageInterest > 0) {
      const deductibleDebtRatio = Math.min(mortgageBalance, maxDebt) / mortgageBalance;
      deductibleMortgageInt = mortgageInterest * deductibleDebtRatio;
    }

    let deductibleHelocInt = 0;

    // ✅ FIX: use spaced label for HELOC purpose
    if (
      helocPurpose === "Home Improvement" &&
      helocBal > 0 &&
      helocInterest > 0
    ) {
      const totalDebt = mortgageBalance + helocBal;
      if (totalDebt <= maxDebt) {
        deductibleHelocInt = helocInterest;
      } else if (mortgageBalance < maxDebt) {
        const remainingLimit = maxDebt - mortgageBalance;
        const deductibleHelocRatio = Math.min(helocBal, remainingLimit) / helocBal;
        deductibleHelocInt = helocInterest * deductibleHelocRatio;
      }
    }

    const totalDeduction = deductibleMortgageInt + deductibleHelocInt;

    const standardDed = standardDeductions[filingStatus] || standardDeductions.Single;
    const isItemizingBeneficial = totalDeduction > standardDed;

    const marginalRate = 0.24;
    const taxSavings = isItemizingBeneficial
      ? (totalDeduction - standardDed) * marginalRate
      : 0;

    const inputs_json = {
      filingStatus,
      acquisitionDate:
        acquisitionDate === "before"
          ? "On or before December 15, 2017"
          : "After December 15, 2017",
      originalMortgageAmount: parseFloat(originalMortgageAmount) || 0,
      currentMortgageBalance: mortgageBalance,
      annualMortgageInterest: mortgageInterest,
      helocBalance: helocBal,
      helocPurpose,
      annualHelocInterest: helocInterest,
      year: Number(year),
      calculatorName,
    };

    const results_json = {
      maxDeductibleDebt: maxDebt,
      deductibleMortgageInterest: deductibleMortgageInt,
      deductibleHelocInterest: deductibleHelocInt,
      totalDeduction,
      standardDeduction: standardDed,
      estimatedTaxSavings: taxSavings,
      itemizingIs: isItemizingBeneficial ? "Beneficial" : "Not Beneficial",
    };

    return { inputs_json, results_json };
  };

  // -------- gate flow --------
  const openGateFlow = () => {
    if (unlocked) {
      setShowConfirmModal(true);
      return;
    }
    setShowLeadModal(true);
  };

  const handleLeadSuccess = ({ email }) => {
    writeUnlock({ email });
    setUnlocked(true);
    setStoredEmail(email || "");
    setShowLeadModal(false);
    setShowSuccessModal(true);
  };

  const handleSuccessViewResults = () => {
    setShowSuccessModal(false);
    scrollToResults();
  };

  // ✅ SAME CHANGE: send results via proxy (like Federal)
  const sendResultsToEmail = async (emailToSend) => {
    const payload = latestPayloadRef.current;
    if (!payload?.inputs_json || !payload?.results_json) return null;

    setSendingResults(true);
    try {
      const resp = await axios.post(
        PROXY_URL,
        {
          path: "/public/send-results",
          method: "POST",
          raw: true,
          qa_mode: qa,
          data: {
            action: "send_results_email",
            email: emailToSend,
            calculator_name: calculatorName,
            calculator_year: String(year),
            page_url: pageUrl,
            timestamp_utc: new Date().toISOString(),
            inputs_json: payload.inputs_json,
            results_json: payload.results_json,
            pdf_notes: {
              disclaimer: mortgageDisclaimerText(year),
            },
            schema_version: 1,
          },
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        }
      );

      if (resp?.data?.success === false) {
        throw new Error(resp?.data?.message || "Failed to send results email");
      }

      setShowConfirmModal(false);
      setShowUpdateEmailModal(false);
      scrollToResults();
      return resp.data;
    } catch (err) {
      console.error("sendResultsToEmail error:", err);
      return null;
    } finally {
      setSendingResults(false);
    }
  };

  const handleConfirmSend = async () => {
    if (!storedEmail) {
      setShowConfirmModal(false);
      setShowUpdateEmailModal(true);
      return;
    }
    await sendResultsToEmail(storedEmail);
  };

  const handleChangeEmail = () => {
    setShowConfirmModal(false);
    setShowUpdateEmailModal(true);
  };

  const handleUpdateEmailAndSend = async (newEmail) => {
    writeUnlock({ email: newEmail });
    setStoredEmail(newEmail);
    await sendResultsToEmail(newEmail);
  };

  const handleViewResultsClick = () => {
    const payload = computePayload();
    latestPayloadRef.current = payload;

    setResults(payload.results_json);
    setHasCalculated(true);

    openGateFlow();
    setTimeout(scrollToResults, 50);
  };

  // ✅ if user taps overlay before calculating, compute first
  const runCalcIfPossible = () => {
    const payload = computePayload();
    latestPayloadRef.current = payload;
    setResults(payload.results_json);
    setHasCalculated(true);
  };

  return (
    <>
      <div className="min-vh-100 position-relative">
        <div className="container py-5">
          <div className="row g-4">
            <div className="col-lg-8">
              {/* Personal Info */}
              <div className="card border-success mb-4">
                <div className="card-header text-success fw-semibold d-flex align-items-center gap-2">
                  <i className="bi bi-person-fill"></i> Personal Information
                </div>

                <div className="card-body">
                  <label className="form-label">Filing Status</label>
                  <select
                    className="form-select border-success"
                    value={filingStatus}
                    onChange={(e) => setFilingStatus(e.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="Single">Single</option>
                    <option value="Married Filing Jointly">Married Filing Jointly</option>
                    <option value="Married Filing Separately">Married Filing Separately</option>
                    <option value="Head of Household">Head of Household</option>
                    <option value="Qualifying Widow(er)">Qualifying Widow(er)</option>
                  </select>
                </div>
              </div>

              {/* Mortgage Info */}
              <div className="card border-warning mb-4">
                <div className="card-header text-warning fw-semibold d-flex align-items-center gap-2">
                  <i className="bi bi-house-door"></i> Mortgage Information
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label">Mortgage Acquisition Date</label>
                    <select
                      className="form-select border-warning"
                      value={acquisitionDate}
                      onChange={(e) => setAcquisitionDate(e.target.value)}
                    >
                      <option value="before">On or before December 15, 2017</option>
                      <option value="after">After December 15, 2017</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Original Mortgage Amount</label>
                    <input
                      type="number"
                      className="form-control border-warning"
                      value={originalMortgageAmount}
                      onChange={(e) => setOriginalMortgageAmount(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Current Mortgage Balance</label>
                    <input
                      type="number"
                      className="form-control border-warning"
                      value={currentMortgageBalance}
                      onChange={(e) => setCurrentMortgageBalance(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="form-label">Annual Mortgage Interest Paid</label>
                    <input
                      type="number"
                      className="form-control border-warning"
                      value={annualMortgageInterest}
                      onChange={(e) => setAnnualMortgageInterest(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* HELOC Info */}
              <div className="card border-purple mb-4">
                <div className="card-header text-purple fw-semibold d-flex align-items-center gap-2">
                  <i className="bi bi-credit-card"></i> Home Equity Loan/Line of Credit (Optional)
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label">HELOC Balance</label>
                    <input
                      type="number"
                      className="form-control border-info"
                      value={helocBalance}
                      onChange={(e) => setHelocBalance(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Purpose of HELOC Funds</label>
                    <select
                      className="form-select border-info"
                      value={helocPurpose}
                      onChange={(e) => setHelocPurpose(e.target.value)}
                    >
                      <option value="Home Improvement">Buy, Build, or Improve Home</option>
                      <option value="Personal Use">Other Personal Use</option>
                      <option value="Investment">Investment Use</option>
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Annual HELOC Interest Paid</label>
                    <input
                      type="number"
                      className="form-control border-info"
                      value={annualHelocInterest}
                      onChange={(e) => setAnnualHelocInterest(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <button
                className="btn w-100"
                onClick={handleViewResultsClick}
                style={{
                  background: "#0f8a4c",
                  borderColor: "#0f8a4c",
                  color: "white",
                  fontWeight: 700,
                }}
              >
                View Results
              </button>
            </div>

            {/* ✅ Results (ALWAYS visible, blurred/locked by default) */}
            <div className="col-lg-4" ref={resultsRef}>
              <div className="unk-results-wrap rounded-4">
                {!unlocked && <div className="unk-bg-blur" />}

                <div style={{ position: "relative", zIndex: 1, width: "100%" }}>
                  <div className={!unlocked ? "unk-results-blur" : ""}>
                    {hasCalculated && results ? (
                      <div className="card bg-primary text-white mb-4">
                        <div className="card-header d-flex align-items-center gap-2">
                          <i className="bi bi-calculator"></i> Your Mortgage Interest Deduction
                        </div>
                        <div className="card-body">
                          <div className="d-flex justify-content-between mb-2">
                            <span>Maximum Deductible Debt:</span>
                            <span>{formatCurrency(results.maxDeductibleDebt)}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Deductible Mortgage Interest:</span>
                            <span>{formatCurrency(results.deductibleMortgageInterest)}</span>
                          </div>
                          {results.deductibleHelocInterest > 0 && (
                            <div className="d-flex justify-content-between mb-2">
                              <span>Deductible HELOC Interest:</span>
                              <span>{formatCurrency(results.deductibleHelocInterest)}</span>
                            </div>
                          )}
                          <hr className="border-light" />
                          <div className="d-flex justify-content-between fw-bold mb-2">
                            <span>Total Mortgage Interest Deduction:</span>
                            <span>{formatCurrency(results.totalDeduction)}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>{year} Standard Deduction:</span>
                            <span>{formatCurrency(results.standardDeduction)}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Itemizing is:</span>
                            <span className="fw-semibold">{results.itemizingIs}</span>
                          </div>
                          {results.itemizingIs === "Beneficial" && (
                            <div className="d-flex justify-content-between">
                              <span>Estimated Tax Savings:</span>
                              <span className="fw-semibold">{formatCurrency(results.estimatedTaxSavings)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="card bg-primary text-white mb-4">
                        <div className="card-header d-flex align-items-center gap-2">
                          <i className="bi bi-calculator"></i> Your Mortgage Interest Deduction
                        </div>
                        <div className="card-body">
                          <div className="d-flex justify-content-between mb-2 opacity-75">
                            <span>Maximum Deductible Debt:</span>
                            <span>$—</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2 opacity-75">
                            <span>Deductible Mortgage Interest:</span>
                            <span>$—</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2 opacity-75">
                            <span>Deductible HELOC Interest:</span>
                            <span>$—</span>
                          </div>
                          <hr className="border-light" />
                          <div className="d-flex justify-content-between fw-bold mb-2 opacity-75">
                            <span>Total Mortgage Interest Deduction:</span>
                            <span>$—</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2 opacity-75">
                            <span>{year} Standard Deduction:</span>
                            <span>$—</span>
                          </div>
                          <div className="d-flex justify-content-between opacity-75">
                            <span>Itemizing is:</span>
                            <span>—</span>
                          </div>

                          <div className="text-white-50 small mt-3">
                            Fill inputs and click View Results to generate your numbers.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {!unlocked && (
                    <div
                      className="unk-results-overlay"
                      onClick={() => {
                        if (!hasCalculated) runCalcIfPossible();
                        openGateFlow();
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          if (!hasCalculated) runCalcIfPossible();
                          openGateFlow();
                        }
                      }}
                      aria-label="Results locked. Open unlock modal."
                    >
                      <div className="unk-overlay-card">
                        <div className="unk-overlay-title">Results Locked</div>
                        <div className="unk-overlay-body">
                          Enter your name and email to view your results instantly.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="card text-center mb-4 mt-3">
                <div className="card-body">
                  <i className="bi bi-currency-dollar fs-3 text-primary mb-2"></i>
                  <h5 className="fw-bold">Need Help with Your Tax Strategy?</h5>
                  <p className="text-muted small">
                    Contact Uncle Kam for professional mortgage interest deduction planning
                    and comprehensive tax advisory services.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary w-100 fw-semibold"
                    onClick={() => window.open(redirectUrl, "_blank", "noopener,noreferrer")}
                  >
                    Get Expert Tax Help
                  </button>
                </div>
              </div>

              <div className="card">
                <div className="card-header text-muted small">Key Assumptions</div>
                <div className="card-body text-muted small">
                  <p>• Based on {year} IRS mortgage interest deduction rules</p>
                  <p>• HELOC interest only deductible if used for home improvement</p>
                  <p>• Tax savings calculated using 24% marginal rate</p>
                  <p>• Does not consider state taxes or AMT</p>
                  <p>• Consult Uncle Kam for personalized tax advice</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Modals ===== */}
      <LeadGateModal
        open={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSuccess={(payload) => handleLeadSuccess(payload)}
        calculatorName={calculatorName}
        calculatorYear={String(year)}
        pageUrl={pageUrl}
        endpoint={endpoint}
        resultsJson={latestPayloadRef.current?.results_json || results}
        inputsJson={
          latestPayloadRef.current?.inputs_json || {
            filingStatus,
            acquisitionDate,
            originalMortgageAmount: parseFloat(originalMortgageAmount) || 0,
            currentMortgageBalance: parseFloat(currentMortgageBalance) || 0,
            annualMortgageInterest: parseFloat(annualMortgageInterest) || 0,
            helocBalance: parseFloat(helocBalance) || 0,
            helocPurpose,
            annualHelocInterest: parseFloat(annualHelocInterest) || 0,
            year: Number(year),
            calculatorName,
          }
        }
        pdf_notes={latestPayloadRef.current?.pdf_notes}
      />

      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onViewResults={handleSuccessViewResults}
      />

      <ConfirmSendResultsModal
        open={showConfirmModal}
        email={storedEmail}
        onClose={() => setShowConfirmModal(false)}
        onViewResults={() => {
          setShowConfirmModal(false);
          scrollToResults();
        }}
        onSend={handleConfirmSend}
        onChangeEmail={handleChangeEmail}
        sending={sendingResults}
      />

      <UpdateEmailModal
        open={showUpdateEmailModal}
        initialEmail={storedEmail}
        onClose={() => setShowUpdateEmailModal(false)}
        onSend={handleUpdateEmailAndSend}
        sending={sendingResults}
        storedEmail={storedEmail}
        endpoint={PROXY_URL}
        resultsJson={latestPayloadRef.current?.results_json || results}
        inputsJson={latestPayloadRef.current?.inputs_json}
        calculatorName={calculatorName}
        calculatorYear={String(year)}
        pageUrl={pageUrl}
        pdf_notes={latestPayloadRef.current?.pdf_notes}
      />
    </>
  );
}

export default MortgageInterestDeductionCalculator;
